import { action, observable, makeObservable } from 'mobx';
import SimplePeer from 'simple-peer';
import {logger} from '../../../lib/helpers';
import {ClassroomRole, SimplePeerSignal, TurnServerCredential} from '../models/classroom-models';
import {SimplePeerInstance} from '../models/simple-peer';
import {ClassroomStoreWrapper} from './parts/ClassroomStoreWrapper';

export class SimplePeerStore {
  readonly role: ClassroomRole;
  readonly teacherConnectionId: string;
  readonly studentConnectionId: string;
  readonly connectionPair: string;

  @observable.ref
  private simplePeer: SimplePeerInstance | null;

  @observable.ref
  remoteStream: MediaStream | null;

  @observable.ref
  simplePeerError: any | null;

  constructor(teacherConnectionId: string,
              studentConnectionId: string,
              readonly stream: MediaStream,
              readonly credential: TurnServerCredential,
              readonly wrapper: ClassroomStoreWrapper) {
    makeObservable(this);
    this.role = wrapper.role;
    this.teacherConnectionId = teacherConnectionId;
    this.studentConnectionId = studentConnectionId;
    this.connectionPair = teacherConnectionId + ':' + studentConnectionId;

    this.simplePeer = null;
    this.remoteStream = null;
    this.simplePeerError = null;

    this.connect();
  }

  @action
  private connect() {
    this.simplePeer = new SimplePeer({
      initiator: this.role === 'Teacher',
      stream: this.stream,
      config: {
        iceServers: [
          {
            urls: 'stun:stun.tilzone.com'
          },
          {
            urls: 'turn:turn.tilzone.com',
            username: this.credential.Username,
            credential: this.credential.Password
          }
        ]
      },
    }) as SimplePeerInstance;
    this.simplePeerError = null;
    this.simplePeer.on('error', this.setSimplePeerError);
    this.simplePeer.on('stream', this.onReceivedStream);
    this.simplePeer.on('signal', this.onSignal);
  }

  @action
  private onReceivedStream = (stream: MediaStream) => {
    logger.log('PeerConnectionStore.onReceivedStream');
    this.remoteStream = stream;
  };

  @action
  private setSimplePeerError = (error: any) => {
    logger.log('PeerConnectionStore.simplePeerError', error);
    this.simplePeerError = error;
    this.remoteStream = null;
  };

  private onSignal = (data: any) => {
    const wrapper = this.wrapper;
    if (wrapper.studentConnectionId !== this.studentConnectionId ||
      wrapper.teacherConnectionId !== this.teacherConnectionId)
      return;

    wrapper.simplePeerSignal(this.teacherConnectionId, this.studentConnectionId, data);
  };

  onReceivedSignal = (signal: SimplePeerSignal) => {
    logger.log('PeerConnectionStore.onReceivedSignal');
    if (this.simplePeer === null)
      return;
    if (this.role === 'Student' &&
      (signal.From !== this.teacherConnectionId || signal.To !== this.studentConnectionId))
      return;
    if (this.role === 'Teacher' &&
      (signal.From !== this.studentConnectionId || signal.To !== this.teacherConnectionId))
      return;
    const payload = JSON.parse(signal.Data);
    this.simplePeer.signal(payload);
  };

  dispose() {
    if (this.simplePeer === null)
      return;
    this.simplePeer.destroy();
    this.simplePeer = null;
    this.simplePeerError = null;
  }

}
